import React, { useMemo } from 'react';
import { Button, Flex, Grid } from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { updateProfileEffect } from 'app/store/Profile/effects';
import { getUpdateLink } from 'app/store/Profile/selectors';
import { AppDispatch, useAppSelector } from 'app/store';
import { Option } from 'types/general.types';
import { dynamicYupResolver, getDirtyValues } from 'app/utils/utils';
import { UnsavedChangesDialog } from 'app/components/Dialogs/UnsavedChangesDialog';
import { transformSchemaToYup } from 'app/utils/schemaToYup';
import { JSONSchema7 } from 'json-schema';
import merge from 'lodash/merge';
import * as yup from 'yup';
import { fieldValidationMessages } from 'app/constants';
import { ProfilePersonalSection } from '../ProfilePersonalSection';
import { ProfileContactSection } from '../ProfileContactSection';
import { ProfileFormData } from './types';

interface ProfileFormProps {
  defaultValues: ProfileFormData;
  dispatch: AppDispatch;
  countries: Option[];
  validationSchema: JSONSchema7;
}

export const ProfileForm: React.FC<ProfileFormProps> = ({
  defaultValues,
  dispatch,
  countries,
  validationSchema,
}) => {
  const profileUpdateLink = useAppSelector(getUpdateLink);
  const mergedValidationSchema = useMemo(() => {
    if (validationSchema?.properties) {
      return merge(validationSchema, {
        required: ['firstName', 'lastName', 'displayName'],
        properties: {
          address: {
            required: defaultValues.address.city ? ['city'] : [],
          },
        },
      });
    }
    return validationSchema;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationSchema]);

  const yupValidationSchema = useMemo(
    () =>
      mergedValidationSchema &&
      transformSchemaToYup<ProfileFormData>(
        mergedValidationSchema,
        defaultValues
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mergedValidationSchema]
  );

  const concatenatedSchema = yup
    .object()
    .shape({
      country: defaultValues.country
        ? yup
            .object()
            .shape({ key: yup.string(), name: yup.string() })
            .required(fieldValidationMessages.REQUIRED)
            .nullable()
        : yup
            .object()
            .shape({ key: yup.string(), name: yup.string() })
            .nullable(),
    })
    .concat(yupValidationSchema);

  const form = useForm<ProfileFormData>({
    defaultValues,
    mode: 'all',
    context: {
      yupValidationSchema: concatenatedSchema,
    },
    resolver: dynamicYupResolver,
  });

  const { handleSubmit, formState, reset } = form;

  const { isDirty, isSubmitting, isValid, dirtyFields } = formState;

  const onValid = (throwError: boolean) => async (data: ProfileFormData) => {
    try {
      const dirtyValues = getDirtyValues<ProfileFormData>(dirtyFields, data);
      await dispatch(updateProfileEffect({ formData: dirtyValues })).unwrap();
      reset(data);
    } catch (e) {
      if (throwError) {
        throw e;
      }
    }
  };

  const getOnSubmit = (throwError = false) => handleSubmit(onValid(throwError));

  return (
    <FormProvider {...form}>
      <form onSubmit={getOnSubmit()} data-testid="profile data form">
        <Flex justifyContent="flex-end" paddingBottom={6}>
          <Button
            variant="secondary"
            size="md"
            lineHeight={4}
            _disabled={{
              opacity: 0.36,
              cursor: 'default',
            }}
            isDisabled={!isDirty || isSubmitting}
            onClick={() => reset(defaultValues)}
          >
            Cancel
          </Button>
          <Button
            size="md"
            lineHeight={4}
            borderRadius={4}
            borderColor="textSubdued"
            marginLeft={2}
            _disabled={{
              opacity: 0.36,
              cursor: 'default',
            }}
            type="submit"
            isDisabled={
              !isDirty || !profileUpdateLink || isSubmitting || !isValid
            }
            data-testid="ProfileForm-save-btn"
          >
            Save
          </Button>
        </Flex>
        <Grid gap={6}>
          <ProfilePersonalSection />
          <ProfileContactSection countries={countries} />
        </Grid>
        <UnsavedChangesDialog
          when={isDirty && !isSubmitting}
          isValid={isValid}
          onSave={getOnSubmit(true)}
        />
      </form>
    </FormProvider>
  );
};
